import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../../api'
import { canauxTarifesDeLOffre, netDuCanal } from './canaux'
import { calcDepuisNet, fmtEuros } from './wizard/StepPrice'
import { libelleUnite } from './wizard/wizardConstants'

/**
 * Modale ✏️ d'une offre — prototype v7.4 (`editFor`), retour Enzo S71 :
 * « quand on appuie sur le crayon, ce n'est pas la modale du prototype ».
 *
 * Forme du prototype : une FEUILLE qui monte (creme, poignee), en-tete emoji +
 * nom en serif + « Modifier mes prix · stock partage : N unite », puis UNE CARTE
 * DE VERRE PAR CANAL TARIFE ACTIF — « Je veux toucher [__] € / unite » et le
 * bloc de transparence en quatre lignes — et un bouton « Termine ». Les canaux
 * ne se choisissent pas ici : « Activez un canal via « Gerer » sur la carte du
 * produit » (prototype), c'est la feuille « Ou vendre ce produit ? ».
 *
 * RIEN D'AUTRE (retour Enzo, S71 : « je veux les champs du prototype ») :
 * photos, nom & marque, description, formats de vente, statut ne s'editent
 * plus ici — le BO les porte. Seule addition : le lien « Supprimer cette
 * offre », demande explicitement le meme jour. Prix stocke = prix de VENTE
 * hors TVA sur commission (convention du wizard S68) : « je veux toucher » =
 * prix × (1 − taux), et inversement.
 *
 * Autonome : porte son etat et appelle l'API ; le parent ne fait que recharger.
 */
function EditOfferModal({ offer, catalogue, onClose, onSaved, onDeleted }) {
  const [nets, setNets] = useState({})        // code canal → saisie « je veux toucher »
  const [saving, setSaving] = useState(false)
  const [erreur, setErreur] = useState(null)

  // Canaux tarifes actifs de l'offre : le principal (prix de base) puis les
  // additionnels actifs (prixCanal, repli prix de base). Taux : celui porte par
  // l'offre / le canal, sinon le defaut du module.
  // Un canal FERME (module non active pour l'organisation, ou lieu non ouvert)
  // n'est pas un endroit ou le produit se vend (Enzo, S72) : pas de carte.
  const tarifes = canauxTarifesDeLOffre(catalogue, offer)

  const fmtSaisie = (n) => (n ? n.toFixed(2).replace('.', ',') : '')

  useEffect(() => {
    if (!offer) return
    const init = {}
    for (const c of tarifes) init[c.code] = fmtSaisie(netDuCanal(c))
    setNets(init)
    setErreur(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer?.id])

  if (!offer) return null

  const unite = libelleUnite(offer.prixUnit || offer.product?.unit)
  const nom = offer.customName || offer.product?.nom || 'Produit'
  const emoji = offer.product?.category?.emoji || '📦'
  const stockLbl = offer.stock != null ? ` · stock partagé : ${Number(offer.stock)} ${libelleUnite(offer.stockUnit || offer.prixUnit)}` : ''

  const netSaisi = (code) => {
    const v = parseFloat((nets[code] || '').replace(',', '.'))
    return isNaN(v) || v <= 0 ? null : v
  }
  const pvDepuisNet = (c) => {
    const net = netSaisi(c.code)
    return net == null ? null : Math.round(calcDepuisNet(net, c.taux).pv * 100) / 100
  }

  const terminer = async () => {
    setSaving(true)
    setErreur(null)
    try {
      const principalT = tarifes.find((c) => c.principal)
      const prixPrincipal = principalT ? pvDepuisNet(principalT) : null
      if (prixPrincipal != null && prixPrincipal !== principalT.prix) {
        await api.producer.updateOffer(offer.id, { prix: prixPrincipal })
      }
      // Prix propre des canaux additionnels, seulement s'il a change.
      for (const c of tarifes) {
        if (c.principal) continue
        const pv = pvDepuisNet(c)
        if (pv != null && pv !== c.prix) await api.producer.setOfferChannel(offer.id, c.code, true, pv)
      }
      onSaved?.()
    } catch (e) {
      setErreur(e.message || 'La mise à jour a échoué')
    } finally {
      setSaving(false)
    }
  }

  const supprimer = async () => {
    if (!confirm('Supprimer cette offre ?')) return
    setSaving(true)
    try {
      await api.producer.deleteOffer(offer.id)
      onDeleted?.()
    } catch (e) {
      setErreur(e.message || 'La suppression a échoué')
      setSaving(false)
    }
  }

  const verre = { background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(7,62,61,0.12)' }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end"
      role="dialog"
      aria-modal="true"
      style={{ background: 'rgba(4,25,24,0.45)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full flex flex-col panel-slide-up"
        style={{ background: 'linear-gradient(170deg, #fdf6ee, rgb(var(--c-creme)))', borderRadius: '30px 30px 0 0', maxHeight: '80vh' }}
      >
        {/* En-tete fixe (prototype) */}
        <div className="px-[18px] pt-4 pb-2 flex-shrink-0">
          <div className="w-[42px] h-1 rounded-full mx-auto mb-3" style={{ background: 'rgba(7,62,61,0.2)' }} />
          <div className="flex items-center gap-2.5">
            <span className="text-2xl leading-none flex-shrink-0">{emoji}</span>
            <div className="flex-1 min-w-0">
              <p className="font-serif text-[20px] leading-tight text-ink truncate">{nom}</p>
              <p className="text-[11.5px] text-ink/55">Modifier mes prix{stockLbl}</p>
            </div>
            <button onClick={onClose} aria-label="Fermer" className="panneau-verre rounded-full px-3 py-[7px] text-[13px] font-bold text-ink flex-shrink-0">✕</button>
          </div>
        </div>

        {/* Corps scrollable */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-[18px] pt-1.5" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
          {tarifes.length === 0 && (
            <p className="text-[13px] text-ink/60 py-2">
              Aucun canal tarifé actif. Activez un canal via « Gérer » sur la carte du produit.
            </p>
          )}
          {tarifes.map((c) => {
            const net = netSaisi(c.code)
            const calc = net != null ? calcDepuisNet(net, c.taux) : null
            return (
              <div key={c.code} className="panneau-verre rounded-[20px] px-4 py-3.5 mb-3">
                <p className="text-[12px] font-extrabold mb-2.5" style={{ color: c.c }}>
                  {c.e} {c.l} · commission {Math.round(c.taux * 100)} %
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-ink/60 flex-shrink-0">Je veux toucher</span>
                  <input
                    value={nets[c.code] ?? ''}
                    inputMode="decimal"
                    onChange={(e) => setNets((n) => ({ ...n, [c.code]: e.target.value }))}
                    placeholder="0,00"
                    className="flex-1 min-w-0 rounded-[14px] px-3.5 py-[11px] text-base font-serif text-ink outline-none"
                    style={verre}
                  />
                  <span className="text-[12.5px] text-ink/60 flex-shrink-0">€ / {unite}</span>
                </div>
                {calc && (
                  <div className="mt-3 rounded-[14px] px-[13px] py-[10px]" style={{ background: 'rgba(7,62,61,0.04)' }}>
                    {[
                      ['Vous touchez (net)', calc.net, true],
                      [`Commission Manrina (${Math.round(c.taux * 100)} %, TVA comprise)`, calc.com, false],
                      ['dont TVA 8,5 % sur la commission', calc.tva, false],
                      ['Le client paie', calc.client, true],
                    ].map(([l, v, fort]) => (
                      <div key={l} className="flex justify-between py-[2.5px]">
                        <span className={`text-[12px] ${fort ? 'font-bold text-ink' : 'font-medium text-ink/65'}`}>{l}</span>
                        <span className={`font-serif ${fort ? 'text-[14.5px] font-bold text-ink' : 'text-[13px] text-ink/70'}`}>{fmtEuros(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}

          {erreur && <p className="mb-3 rounded-xl px-3 py-2.5 text-xs bg-red-50 text-red-600">{erreur}</p>}

          <button
            onClick={terminer}
            disabled={saving}
            className="w-full rounded-full text-[14px] font-extrabold active:scale-[0.98] transition-transform disabled:opacity-60"
            style={{ padding: '14px 0', background: 'rgb(var(--c-green))', color: 'rgb(var(--c-creme))' }}
          >
            {saving ? 'Enregistrement…' : 'Terminé'}
          </button>

          <button onClick={supprimer} disabled={saving} className="w-full mt-3 py-2 text-[12.5px] font-bold text-[#d9534f] disabled:opacity-50">
            Supprimer cette offre
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default EditOfferModal
